import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { Text as EmailText } from '@react-email/editor/extensions';
import { UndoRedo } from '@tiptap/extensions';
import { Text } from '@mantine/core';
import { EditorContent, useEditor } from '@tiptap/react';
import { forwardRef, useImperativeHandle, useMemo } from 'react';
import styled from 'styled-components';

import { LiquidVariable } from '../extensions/liquidVariable';
import { SubjectDocument } from '../extensions/subjectDocument';
import { TemplateVariablesContext } from '../utils/templateVariablesContext';
import { subjectToContent, subjectToText } from '../utils/subjectContent';

export interface SubjectHandle {
	insertVariable: (name: string) => void;
}

interface Props {
	value: string;
	variables: string[];
	editable?: boolean;
	onChange: (value: string) => void;
	onFocus: () => void;
}

const Block = styled.div`
	padding: 1.25rem 1.5rem 0.75rem;
	border-bottom: 1px solid var(--mantine-color-gray-4);
	border-radius: var(--mantine-radius-md) var(--mantine-radius-md) 0 0;
	background-color: rgba(244, 245, 246, 0.4);
`;

const SubjectField = styled(EditorContent)`
	.ProseMirror {
		min-height: 2rem;
		padding: 0.3125rem 0;
		font-size: 0.8125rem;
		font-weight: 500;
		line-height: 1.375rem;
		color: var(--mantine-color-text);
		white-space: nowrap;
		overflow-x: auto;
		outline: none;
	}
`;

const EXTENSIONS = [SubjectDocument, EmailText, LiquidVariable, UndoRedo];

export const EmailSubjectBlock = forwardRef<SubjectHandle, Props>(
	({ value, variables, editable = true, onChange, onFocus }, ref) => {
		const tMipManage = useTranslationMipManage();
		const emailSubjectLabel = tMipManage('email_templates.editor.email_subject_label', {
			defaultValue: 'Email subject',
		});
		const initialContent = useMemo(() => subjectToContent(value), []);

		const editor = useEditor({
			extensions: EXTENSIONS,
			content: initialContent,
			editable,
			immediatelyRender: false,
			editorProps: {
				attributes: { 'aria-label': emailSubjectLabel, role: 'textbox' },
				handleKeyDown: (_view, event) => {
					if (event.key !== 'Enter') {
						return false;
					}
					event.preventDefault();
					return true;
				},
			},
			onUpdate: ({ editor: current }) => onChange(subjectToText(current)),
			onFocus,
		});

		useImperativeHandle(
			ref,
			() => ({
				insertVariable: (name: string) => {
					editor
						?.chain()
						.focus()
						.insertContent({ type: 'liquidVariable', attrs: { name } })
						.run();
				},
			}),
			[editor]
		);

		return (
			<Block>
				<Text fz="0.75rem" c="gray.9" mb={4}>
					{emailSubjectLabel}
				</Text>
				<TemplateVariablesContext.Provider value={variables}>
					<SubjectField editor={editor} />
				</TemplateVariablesContext.Provider>
			</Block>
		);
	}
);

EmailSubjectBlock.displayName = 'EmailSubjectBlock';
